import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Container, 
  Grid,
  CircularProgress,
  Typography
} from '@mui/material';
import './LandingPage.module.css';
import PostCard from './Post/PostCard';
import PostModal from './Post/PostModal';
import Header from './Header/Header';
import SocialProfiles from './Sidebar/SocialProfiles';
import TrendingSearches from './Sidebar/TrendingSearches';
import { fetchNotionPosts, NotionPost } from '../../services/notionService';
import { mockPosts } from './MockPosts';
import styles from './LandingPage.module.css';

const LandingPage: React.FC = () => {
  const [selectedPost, setSelectedPost] = useState<any>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [posts, setPosts] = useState<NotionPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filteredPosts, setFilteredPosts] = useState<NotionPost[]>([]);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);

  useEffect(() => {
    const loadPosts = async () => {
      try {
        setLoading(true);
        const notionPosts = await fetchNotionPosts();
        const postsData = notionPosts.length > 0 ? notionPosts : mockPosts;
        setPosts(postsData);
        setFilteredPosts(postsData);
        setError(null);
      } catch (err) {
        console.error('Failed to load posts:', err);
        setError('Failed to load posts from Notion');
        setPosts(mockPosts);
        setFilteredPosts(mockPosts);
      } finally {
        setLoading(false);
      }
    };

    loadPosts();
  }, []);

  useEffect(() => {
    if (selectedTags.length === 0) {
      setFilteredPosts(posts);
    } else {
      const filtered = posts.filter(post =>
        selectedTags.some(tag => post.tags.includes(tag))
      );
      setFilteredPosts(filtered);
    }
  }, [selectedTags, posts]);

  const handlePostClick = (post: any) => {
    setSelectedPost(post);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setTimeout(() => setSelectedPost(null), 300);
  };

  const handleTagClick = (tag: string) => {
    setSelectedTags([tag]);
  };

  const allTags = Array.from(new Set(posts.flatMap(post => post.tags)));

  if (loading) {
    return (
      <Box className={styles.landingPage}>
        <Header />
        <Container maxWidth="xl" className={styles.loadingContainer}>
          <CircularProgress />
        </Container>
      </Box>
    );
  }

  return (
    <Box className={styles.landingPage}>
      <Header />
      
      <Container maxWidth="xl" className={styles.mainContainer}>
        {error && (
          <Typography color="error" className={styles.errorText}>
            {error} - Showing mock data
          </Typography>
        )}
        
        <Grid container spacing={3}>
          {/* Left Section - 3 columns of posts */}
          <Grid item xs={12} lg={9}>
            <Grid container spacing={3}>
              {filteredPosts.slice(0, 3).map((post) => (
                <Grid item xs={12} sm={6} md={4} key={post.id}>
                  <PostCard post={post} onClick={() => handlePostClick(post)} />
                </Grid>
              ))}
            </Grid>
          </Grid>

          {/* Right Section - Sidebar */}
          <Grid item xs={12} lg={3}>
            <Box className={styles.sidebar}>
              <SocialProfiles />
              <TrendingSearches tags={allTags} onTagClick={handleTagClick} />
            </Box>
          </Grid>

          {/* Full Width Posts Below */}
          <Grid item xs={12}>
            <Grid container spacing={3}>
              {filteredPosts.slice(3).map((post) => (
                <Grid item xs={12} sm={6} md={3} key={post.id}>
                  <PostCard post={post} onClick={() => handlePostClick(post)} />
                </Grid>
              ))}
            </Grid>
          </Grid>
        </Grid>
      </Container>

      <PostModal 
        post={selectedPost} 
        open={modalOpen} 
        onClose={handleCloseModal}
      />
    </Box>
  );
};

export default LandingPage;